<template>
	<div
		:class="[
			$style['app-text-field-container'],
			{
				[$style['app-text-field-container--multiple']]: multiple
			}
		]"
	>
		<div

			class="input"
			:class="[
				$style['app-text-field'],
				$style['app-text-field--dense'],
				{
					[$style['app-text-field--invalid']]: invalid,
					[$style['app-text-field--disabled']]: isDisabled,
					[$style['app-text-field--focused']]: isFocused
				}
			]"
		>
			<VueGoogleAutocomplete
				:id="'a' + _uid"
				ref="autocomplete"
				v-bind="$attrs"
				placeholder=""
				autocomplete="off"
				:name="_uid"
				:classname="$style['app-text-field__input']"
				@focus="inputFocusHandler"
				@blur="inputBlurHandler"
				@input-change="inputInputHandler"
				@placechanged="handlePlaceChanged"
			/>
			<FloatingLabel
				:class="$style['app-text-field__label']"
				:label-text="labelText"
				:required="isRequired"
				:is-focused="isFocused"
				:is-floated="isFloated"
			/>
			<NotchedOutline
				:class="$style['app-text-field__outline']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
			<NotchedOutlineIdle
				:class="$style['app-text-field__idle']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
		</div>
		<p
			v-if="helperText"
			:class="$style['app-text-field-helper-text']"
		>
			<template v-if="helperText">
				{{ helperText }}
			</template>
		</p>
	</div>
</template>
<script>
import NotchedOutline from '@c:essentials/NotchedOutline'
import NotchedOutlineIdle from '@c:essentials/NotchedOutlineIdle'
import FloatingLabel from '@c:essentials/FloatingLabel'
import VueGoogleAutocomplete from '@/components/Essentials/GoogleAutocompleteInput'
import requiredField from '@/mixins/requiredField'
export default {
	name: 'AutoCompleteField',
	components: {
		NotchedOutline,
		NotchedOutlineIdle,
		FloatingLabel,
		VueGoogleAutocomplete
	},
	mixins: [requiredField],
	inheritAttrs: false,
	props: {
		maxLength: {
			type: [String, Number],
			default: ''
		},
		icon: {
			type: [String, Number],
			default: ''
		},
		placeholder: {
			type: [String, Number],
			default: ''
		},
		value: {
			type: [String, Number, Object],
			default: null
		},
		labelText: {
			type: String,
			default: ''
		},
		required: {
			type: [Boolean, String],
			default: false
		},
		helperText: {
			type: String,
			default: null
		},
		multiple: {
			type: [Boolean, String],
			default: false
		},
		invalid: {
			type: [Boolean, String],
			default: false
		},
		maxCharacters: {
			type: Number,
			default: 0
		}
	},
	data: () => ({
		isFocused: false,
		isFloated: false,
		privateValue: null
	}),
	computed: {
		isDisabled () {
			return this.$attrs.disabled === '' || this.$attrs.disabled
		}
	},
	watch: {
		value: {
			immediate: false,
			handler () {
				const stringValue = typeof this.value === 'object' && this.value !== null
					? (this.value.formatted_address || this.value.name || String(this.value))
					: this.value
				if (stringValue !== null && stringValue !== '') {
					this.isFloated = true
					this.$refs.autocomplete.update(stringValue)
				} else if (!this.isFocused) {
					this.$refs.autocomplete.update(null)
					this.isFloated = false
				}
			}
		}

	},
	mounted () {
		const stringValue = typeof this.value === 'object' && this.value !== null
			? (this.value.formatted_address || this.value.name || String(this.value))
			: this.value
		if (stringValue !== null && stringValue.length) {
			this.isFloated = true
			this.$refs.autocomplete.update(stringValue)
		}
	},
	methods: {
		inputFocusHandler (event) {
			this.isFocused = true
			this.isFloated = true
			this.$emit('focus', event)
		},
		inputBlurHandler (event) {
			this.isFocused = false
			this.isFloated = !!(this.value || this.placeholder)
			this.$emit('blur', event)
		},
		inputInputHandler ({ newVal }) {
			if (newVal !== null && newVal !== '') {
				this.isFloated = true
			} else if (!this.isFocused) {
				this.isFloated = false
			}
			this.$emit('notched-outline:update')
			this.$emit('input', newVal)
		},
		getStreetText (place) {
			const components = place.address_components || []
			const findComponent = type => components.find(c => c.types.includes(type))

			const streetTypes = ['street_address', 'premise', 'subpremise', 'route']
			if (place.name && place.types?.some(t => streetTypes.includes(t))) return place.name

			const formatted = place.formatted_address || ''
			const route = findComponent('route')
			const routeName = route && [route.long_name, route.short_name].find(n => formatted.includes(n))
			if (routeName) {
				const routeStart = formatted.indexOf(routeName)
				const routeEnd = routeStart + routeName.length
				const distance = i => Math.min(Math.abs(i - routeEnd), Math.abs(i - routeStart))
				const ranges = [[routeStart, routeEnd]]
				;[findComponent('street_number'), findComponent('subpremise')].filter(Boolean).forEach(({ long_name: value }) => {
					const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
					const closest = [...formatted.matchAll(new RegExp(`(^|[\\s,\\-/#])(${escaped})(?=$|[\\s,\\-/])`, 'g'))]
						.map(m => m.index + m[1].length)
						.sort((a, b) => distance(a) - distance(b))[0]
					if (closest !== undefined) ranges.push([closest, closest + value.length])
				})
				return formatted.slice(Math.min(...ranges.map(r => r[0])), Math.max(...ranges.map(r => r[1])))
			}

			return (this.$refs.autocomplete.autocompleteText || formatted).split(',')[0]
		},
		withTypedHouseNumber (street, place, typedText) {
			const number = place.address_components?.find(c => c.types.includes('street_number'))?.long_name
			if (!number || !typedText) return street
			const escaped = number.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
			const typedNumber = typedText.match(new RegExp(`(?:^|[\\s,])(${escaped}(?:[-/][\\p{L}\\d]+|\\p{L}{1,3})+)(?=$|[\\s,])`, 'u'))?.[1]
			if (!typedNumber || street.includes(typedNumber)) return street
			return street.replace(new RegExp(`(^|[\\s,])${escaped}(?=$|[\\s,])`), (_, prefix) => `${prefix}${typedNumber}`)
		},
		handlePlaceChanged (addressData, placeResultData, _id) {
			const typedText = this.$refs.autocomplete.autocompleteText
			this.$emit('place-changed', { addressData, placeResultData })
			this.$nextTick(() => {
				const text = this.withTypedHouseNumber(this.getStreetText(placeResultData), placeResultData, typedText)
				this.$refs.autocomplete.update(text)
				this.$emit('input', text)
				this.isFloated = true
			})
		}
	}
}
</script>
<style lang="scss" module>
.app-text-field-container {
    text-overflow: ellipsis;
    position: relative;
    width: 100%;

    &--multiple {
        padding: 0 $gutter * 1.25;
    }
}

.app-text-field-helper-text {
    position: relative;
    bottom: 10px;
    display: flex;
    align-items: center;
    margin: 0;
    margin-bottom: calc($gutter / 2);
    color: getColor(helper-text);
    font-size: 12px;
    line-height: 1.25rem;
    width: 100%;

    &--right {
        text-align: right;
        justify-self: flex-end;
        flex: 1;
    }
}

.app-text-field {
    position: relative;
    display: inline-flex;
    width: 100%;
    align-items: flex-end;
    box-sizing: border-box;
    will-change: opacity, transform, color;
    margin: $gutter * 1.25 0;
    height: 56px;
    border: none;
    z-index: 2;

    &__label {
        z-index: 1;
    }

    .app-text-field &__clear {
        position: absolute;
        right: 15px;
        top: 50%;
        transform: translateY(-50%);
        z-index: 3;
        color: getColor(grey);
        font-size: 13px;
    }

    &__input {
        z-index: 2;
        display: flex;
        box-sizing: content-box;
        border: none;
        border-radius: 0;
        background-color: transparent;
        font-size: 16px;
        line-height: 1.75rem;
        font-weight: 400;
        letter-spacing: 0.0094em;
        width: 100%;
        height: 30px;
        transition: opacity 0.18s cubic-bezier(0.4, 0, 0.2, 1);
        caret-color: getColor(tertiary);

        &:focus {
            outline: none;
        }

        &:not(:focus, :disabled):hover {
            & ~ .app-text-field__outline {
                border-color: getColor(text);
            }

            & ~ .app-text-field__idle {
                border-color: getColor(text);
            }
        }

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus {
            animation-name: autofill;
            animation-fill-mode: both;
        }

        &[type="number"]::-webkit-inner-spin-button,
        &[type="number"]::-webkit-outer-spin-button {
            appearance: none;
            margin: 0;
        }
    }

    &:not(.app-text-field--disabled) {
        & .app-text-field__idle {
            background-color: getColor(input-background);
        }

        & .app-text-field__outline {
            background-color: getColor(input-background);
        }

        .app-text-field__input {
            color: getColor(text);
        }
    }

    &--dense {
        height: 48px;

        .app-text-field__input {
            padding: $gutter $gutter * 2;
        }
    }

    &--no-helper {
        margin-bottom: $gutter * 1.25;
    }

    &--disabled {
        .app-text-field__idle {
            background-color: getColor(input-background-disabled);
        }

        .app-text-field__outline {
            background-color: getColor(input-background-disabled);
        }

        .app-text-field__label {
            color: getColor(input-label-disabled);
        }

        .app-text-field__input {
            color: getColor(helper-text);
        }
    }

    &--invalid {
        .app-text-field__input {
            caret-color: getColor(danger);
        }

        .app-text-field__outline {
            border-color: getColor(danger) !important;
        }

        .app-text-field__idle {
            border-color: getColor(danger) !important;
        }

        .app-text-field__label {
            color: getColor(danger) !important;
        }

        & ~ .app-text-field-helper-text {
            color: getColor(danger) !important;
        }
    }
}

@keyframes autofill {
    to {
        color: inherit;
        background: transparent;
    }
}
</style>
